import { z } from 'zod';
import { newPassword } from './auth';
import { email } from './common';

// Phase I (I-A): the SaaS tenant lifecycle, sign-up and onboarding, shared by the API and web.

export const TENANT_STATUSES = ['trial', 'active', 'past_due', 'suspended', 'cancelled', 'pending_deletion'] as const;
/** Mirrors the Prisma enum `TenantStatus`. */
export type TenantLifecycleStatus = (typeof TENANT_STATUSES)[number];

export const TENANT_STATUS_LABELS: Record<TenantLifecycleStatus, string> = {
  trial: 'Free trial',
  active: 'Active',
  past_due: 'Payment overdue',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
  pending_deletion: 'Scheduled for deletion',
};

/** Days a past-due tenant keeps full use before it is suspended. */
export const PAST_DUE_GRACE_DAYS = 14;
/** Days between scheduling a deletion and the purge. */
export const DELETION_DELAY_DAYS = 30;

// --- Sign-up -------------------------------------------------------------------------------

/**
 * Words a tenant may not take as its slug (subdomain and company code): hostnames the platform
 * uses or may use, and names that would make a convincing phishing subdomain.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  'www', 'api', 'app', 'apps', 'admin', 'administrator', 'platform', 'status', 'domains', 'domain',
  'mail', 'email', 'smtp', 'imap', 'pop', 'pop3', 'mx', 'webmail', 'autodiscover', 'autoconfig',
  'ftp', 'sftp', 'ssh', 'vpn', 'ns', 'ns1', 'ns2', 'dns', 'cdn', 'static', 'assets', 'img', 'images',
  'media', 'files', 'download', 'downloads', 'upload', 'uploads', 'portal', 'login', 'signin',
  'signup', 'register', 'auth', 'oauth', 'sso', 'account', 'accounts', 'billing', 'pay', 'payment',
  'payments', 'invoice', 'invoices', 'support', 'help', 'helpdesk', 'docs', 'documentation', 'blog',
  'news', 'about', 'contact', 'legal', 'terms', 'privacy', 'security', 'trust', 'abuse', 'postmaster',
  'hostmaster', 'webmaster', 'root', 'system', 'sys', 'internal', 'staff', 'team', 'test', 'testing',
  'demo', 'dev', 'development', 'stage', 'staging', 'prod', 'production', 'preview', 'sandbox', 'beta',
  'alpha', 'console', 'dashboard', 'manage', 'manager', 'probe', 'monitor', 'monitoring', 'metrics',
  'health', 'ws', 'socket', 'webhook', 'webhooks', 'hooks', 'ext', 'extension', 'mobile', 'm', 'public',
  'share', 'shares', 'q', 'denwen-msp', 'denwenmsp', 'flowmsp', 'flow-msp', 'official', 'null', 'undefined',
]);

/** 3 to 40 lower-case letters, digits and single hyphens, starting and ending with a letter or digit. */
export const signupSlug = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Use at least 3 characters')
  .max(40, 'Use at most 40 characters')
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Use letters, digits and hyphens, starting and ending with a letter or digit')
  .refine((s) => !s.includes('--'), 'Use single hyphens only');

/** A slug suggestion from a company name ("Acme IT Ltd." → "acme-it"). */
export function suggestSlug(companyName: string): string {
  const words = companyName
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter((w) => w && !['ltd', 'limited', 'llc', 'inc', 'plc', 'llp', 'gmbh', 'co'].includes(w));
  return words.join('-').slice(0, 40).replace(/-+$/, '');
}

export const signupInput = z.object({
  companyName: z.string().trim().min(2, 'Enter your company name').max(100),
  slug: signupSlug,
  ownerName: z.string().trim().min(1, 'Enter your name').max(100),
  email,
  password: newPassword,
  /** The CAPTCHA widget's token, when the CAPTCHA is on. */
  captchaToken: z.string().max(4000).optional(),
  acceptTerms: z.literal(true, { error: 'Accept the terms to continue' }),
});
export type SignupInput = z.input<typeof signupInput>;

export const signupTokenInput = z.object({ token: z.string().min(20).max(200) });

export interface SignupConfig {
  enabled: boolean;
  trialDays: number;
  captcha: { provider: 'turnstile' | 'hcaptcha'; siteKey: string } | null;
}

export interface SlugCheck {
  slug: string;
  available: boolean;
  reason?: string;
}

export type SignupProgressStatus = 'verified' | 'provisioning' | 'ready' | 'failed';

export interface SignupProgress {
  status: SignupProgressStatus;
  step: string | null;
  percent: number;
  error: string | null;
}

// --- Onboarding ----------------------------------------------------------------------------

export const ONBOARDING_STEPS = [
  { key: 'company', label: 'Company details', description: 'Your company name, address, time zone and currency.' },
  { key: 'branding', label: 'Branding', description: 'Your logo and accent colour, shown in the portal and on emails.' },
  { key: 'technicians', label: 'Invite technicians', description: 'Add the people on your team.' },
  { key: 'clients', label: 'Import clients', description: 'Upload a CSV of your clients, or use one of the importers.' },
  { key: 'mailbox', label: 'Support mailbox', description: 'Turn emails to your support address into tickets.' },
  { key: 'sample', label: 'Sample data', description: 'Try Denwen MSP with a few example clients and tickets.' },
] as const;
export type OnboardingStepKey = (typeof ONBOARDING_STEPS)[number]['key'];
export const ONBOARDING_STEP_KEYS = ONBOARDING_STEPS.map((s) => s.key) as [OnboardingStepKey, ...OnboardingStepKey[]];

export type OnboardingStepState = 'pending' | 'done' | 'skipped';

export interface OnboardingState {
  steps: Record<OnboardingStepKey, OnboardingStepState>;
  /** The owner closed the wizard (it can be reopened from the dashboard). */
  dismissed: boolean;
  sampleData: boolean;
  completedAt: string | null;
}

export const onboardingStepInput = z.object({ state: z.enum(['done', 'skipped', 'pending']) });

export const onboardingCompanyInput = z.object({
  name: z.string().trim().min(2).max(100),
  address: z.string().trim().max(500).nullish().transform((v) => v || null),
  timezone: z.string().trim().min(1).max(64).refine((tz) => {
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }, 'Choose a valid time zone'),
  currency: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, 'Use a 3-letter currency code'),
});
export type OnboardingCompanyInput = z.input<typeof onboardingCompanyInput>;

export const onboardingBrandingInput = z.object({
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a colour like #2563eb'),
});

export const onboardingInviteInput = z.object({
  invites: z
    .array(z.object({ name: z.string().trim().min(1).max(150), email }))
    .min(1, 'Add at least one person')
    .max(25),
});

export interface ClientImportResult {
  created: number;
  skipped: { row: number; reason: string }[];
}

export const sampleDataInput = z.object({ enabled: z.boolean() });
